/**
 * Calculates the offset of an element within a scrolling container.
 *
 * This function computes the horizontal and vertical offset of an element relative to
 * the given scrolling container. The offset takes into account the scroll position of
 * the container, providing a position relative to the current view within the container.
 *
 * @param {HTMLElement} element - The element for which to calculate the offset.
 * @param {Element} scrollingContainer - The scrolling container element within which the offset is to be calculated.
 * @returns {{ offsetX: number, offsetY: number }} An object containing the horizontal (`offsetX`)
 *          and vertical (`offsetY`) offsets of the element within the container.
 *
 * @example
 * const myElement = document.getElementById('exampleElement');
 * const myScrollingContainer = document.getElementById('exampleContainer');
 * const offset = getOffsetWithinScrollingContainer(myElement, myScrollingContainer);
 * console.log(`Offset X: ${offset.offsetX}, Offset Y: ${offset.offsetY}`);
 */
function getOffsetWithinScrollingContainer(element: HTMLElement, scrollingContainer: Element) {
  // Get the bounding rectangle of the element
  const elementRect = element.getBoundingClientRect()

  // Get the bounding rectangle of the scrolling container
  const containerRect = scrollingContainer.getBoundingClientRect()

  // Calculate the offset
  const offsetX = elementRect.left - containerRect.left + scrollingContainer.scrollLeft
  const offsetY = elementRect.top - containerRect.top + scrollingContainer.scrollTop

  return { offsetX, offsetY }
}

interface IArgs {
  containerElement: HTMLElement
  calendarHeight: number
}

interface Position {
  top?: number
  bottom?: number
}

type IResultPosition = Position

/**
 * Determines the optimal position for a calendar component within a Handsontable container.
 *
 * The function calculates the available space in the scroll area and viewport to place the calendar
 * either above or below the target container element. It returns the appropriate position style
 * properties for positioning the calendar.
 *
 * @param {IArgs} { containerElement, calendarHeight } - The arguments object containing the target container element,
 *                                                       and the height of the calendar component.
 * @returns {IResultPosition} - An object with positioning style properties or an empty object if
 *                              no suitable position is found. The properties can include `top` or `bottom`
 *                              which specify the offset distance in pixels for positioning the calendar.
 *
 * @example
 * const position = determineCalendarPosition({ containerElement: myContainer });
 * // Apply `position` to the calendar component's style for positioning
 */
export function determineCalendarPosition({
  containerElement,
  calendarHeight,
}: IArgs): IResultPosition {
  const CALENDAR_HEIGHT = calendarHeight
  const OFFSET_FOR_HEADER = 264
  const CELL_HEIGHT = 36
  // Find our Handsontable and its scroll area
  const masterParent = containerElement?.closest('div.handsontable-container')
  const tableViewportElement = masterParent?.querySelector('div.wtHolder')
  const tableScrollArea = masterParent?.querySelector('div.wtHider')

  if (!containerElement || !tableScrollArea || !tableViewportElement) return {}

  // Get container structures
  const containerRect = containerElement?.getBoundingClientRect()
  const tableViewportRect = tableViewportElement?.getBoundingClientRect()
  const tableScrollAreaRect = tableScrollArea?.getBoundingClientRect()

  const { offsetY } = getOffsetWithinScrollingContainer(containerElement, tableScrollArea)

  const tableViewportHeight = tableViewportRect.height
  const scrollHeight = tableScrollAreaRect.height
  const topX = Math.floor(containerRect.top)

  const spaceAvailableBelowInScrollArea = scrollHeight - offsetY - CELL_HEIGHT - CALENDAR_HEIGHT > 0
  const spaceAvailableAboveInScrollArea = offsetY - CALENDAR_HEIGHT - CELL_HEIGHT > 0
  const spaceAvailableAboveViewport = topX - OFFSET_FOR_HEADER - CALENDAR_HEIGHT > 0
  const spaceAvailableBelowViewport =
    topX - OFFSET_FOR_HEADER + CALENDAR_HEIGHT < tableViewportHeight

  if (spaceAvailableAboveInScrollArea && !spaceAvailableBelowViewport) {
    return { top: -CALENDAR_HEIGHT }
  }

  if (spaceAvailableAboveInScrollArea && !spaceAvailableBelowInScrollArea) {
    return { top: -CALENDAR_HEIGHT }
  }

  if (
    spaceAvailableAboveInScrollArea &&
    spaceAvailableBelowInScrollArea &&
    spaceAvailableAboveViewport &&
    !spaceAvailableBelowViewport
  ) {
    return { bottom: CELL_HEIGHT }
  }

  if (
    spaceAvailableAboveInScrollArea &&
    spaceAvailableBelowInScrollArea &&
    !spaceAvailableAboveViewport &&
    !spaceAvailableBelowViewport
  ) {
    return { top: -CALENDAR_HEIGHT }
  }

  return {}
}
